import { DesktopDatePicker, DesktopTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import './CreateSchedulePta.scss';
import dayjs from 'dayjs';
import moment from 'moment-timezone';
import { Controller, FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { FC, ReactNode, useEffect, useState } from 'react';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useLazyQuery, useMutation } from '@apollo/client';
import ToasterWithMessage from '../toaster';
import images from '../../assets/images';
import Select, { StylesConfig } from 'react-select';
import { CREATE_PTA_MEETING, EDIT_PTA_MEETING, GET_ALL_MEETING_CATEGORIES } from '../../pages/AdminPta/api/PtaQuery';
import { PtaMeetType } from '../../pages/PtaMeetings/PtaMeetings';
import { MeetingCategoryType } from '../../pages/AdminPta/AdminPta';
import { COLOR_WHITE, FETCH_POLICY, JITSI, messages } from '../../utils/constants';
import { useAppSelector } from '../../redux/app/hooks';
import { getScreenDimensions } from '../../utils/helper';
import { trackButtonClick } from '../../firebase/analyticsHelper';
import { ButtonClickEvents } from '../../firebase/constants/events';

dayjs.extend(utc);
dayjs.extend(timezone);


interface IFormInput {
    description: string;
    startTime: any;
    endTime: any;
    dateValue: any;
    timeRange?: string;
    category: string;
}

interface CategoryOption {
    value: string;
    label: string;
}

// Define Yup schema
const schema = yup.object().shape({
    description:yup
    .string()
    .trim()
    .required('Description is required')
    // .min(5,'Description must be between 5 and 150 characters long')
    .max(150, 'Description must be between 1 and 150 characters long'),
    dateValue: yup.date().required('Date is required'),
    startTime: yup
        .string()
        .required('Start time is required')
        // .uppercase()
        // .matches(/^(0[1-9]|1[0-2]):[0-5][0-9] [AP]M$/, 'Invalid time format or Hour must not be 0')
        .test('is-after-now', messages.meetingStartTime, function (value:any) {
          const { meetService } = this.options.context as { meetService: string };
          console.log("[test] meetService", meetService);
          if (meetService !== 'JITSI') return true; // only validate for JITSI
          const { dateValue } = this.parent;
          if (!value || !dateValue) {
            return true;
          }
          const selectedDate = dayjs(dateValue);
          const currentTime = dayjs();
          const selectedTime = dayjs(value, 'hh:mm A');
          if (selectedDate.isSame(currentTime, 'day')) {
            const diffMinutes = selectedTime.diff(currentTime, 'minute');
            return diffMinutes >= 6;
          }
          return true;
        }),
    endTime: yup
        .string()
        .required('End time is required')
        .uppercase()
        // .matches(/^(0[1-9]|1[0-2]):[0-5][0-9] [AP]M$/, 'Invalid time format or Hour must not be 0')
        .test('is-after-now', 'End time must be in the future', function (value: any) {
            const { dateValue } = this.parent;
            if (!value || !dateValue) {
                return true;
            }
            const selectedDate = dayjs(dateValue);
            const currentTime = dayjs();
            const selectedTime = dayjs(value, 'hh:mm A');
            if (selectedDate.isSame(currentTime, 'day')) {
                return selectedTime.isAfter(currentTime);
            }
            return true;
        }),
    timeRange: yup.string().test('is-valid-time-range', 'End time must be later than start time', function (value, context) {
                const { startTime, endTime } = context.parent;
                if (!startTime || !endTime) {
                    return true; // Skip this validation if either time is not set
                }
                const start = dayjs(startTime, 'hh:mm A');
                const end = dayjs(endTime, 'hh:mm A');
                return end.isAfter(start);
    }),
    category: yup.string().required("Meeting category is required")
});

const formatStartAndEndTime = (timing: string) => {
    return moment.utc(timing).local().format('hh:mm A');
}

interface PropsType {
    setShowCreateScheduling: (val:boolean) => void;
    clickedSessionDetails?: PtaMeetType;
    setEditClicked?: (val: boolean) => void; 
    editClicked?: boolean;
    fetchUpcomingSessions?: () => void;
    setOpenConfirmationPopup?: (val: boolean) => void; 
}
const CreateSchedulePta: FC<PropsType> = ({setShowCreateScheduling, editClicked, setEditClicked, clickedSessionDetails, fetchUpcomingSessions, setOpenConfirmationPopup}) => {

    const meetService = useAppSelector((state) => state.auth.meetService);
    const methods = useForm<IFormInput>({
        resolver: yupResolver(schema),
        context: { meetService },
    });
    const { teacherDeleteIcon } = images;
    const { register, handleSubmit, control, trigger, reset, getValues, setValue, formState: { errors, isSubmitted }, clearErrors } = methods;
    const dateValue = useWatch({ control, name: 'dateValue' });
    const startTime = useWatch({ control, name: 'startTime' });
    const endTime = useWatch({ control, name: 'endTime' });
    const { error, success } = ToasterWithMessage();
    const [categoryList, setCategoryList] = useState<CategoryOption[]>([]);
    const { isMobileScreen } = getScreenDimensions();

    // Define your custom styles
    const customStyle = {
        '& .MuiInputBase-root': {
            backgroundColor: isMobileScreen ? COLOR_WHITE : '#f0f3f5',
            width: '113px',
            height: '35px',
            outline: 'none',
            border: 'none',
            '&:focus': {
                outline: 'none',
                border: 'none'
            },
            '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
            },
            '& .MuiInputBase-input::placeholder': {
                opacity: 1,
                fontFamily: 'Inter',
                color: '#616161', 
                fontSize: '12px',
                fontWeight: 400,
                '&:focus': {
                fontSize: '12px',
                color: '#616161', 
            },
            },
            '& .MuiInputBase-input': {
                fontSize: '12px', 
            },
            '& .MuiInputAdornment-root': {
                marginLeft: '0px',
            },
            '& .MuiInputBase-input.Mui-disabled': {
                opacity: 1,
                WebkitTextFillColor: 'unset',
                color: '#616161'
            },
        },
    };

    const customStyleSelect: StylesConfig = {
        control: (styles) => ({
          ...styles,
          cursor: 'pointer',
          height: '30px',
          padding: '0px',
          minHeight: '27px',
          border: 'none',
          backgroundColor: isMobileScreen ? COLOR_WHITE : '#f0f3f5',
          outline: 'none'
        }),
        indicatorSeparator: (styles) => ({
          ...styles,
          display: 'none'
        }),
        indicatorsContainer: (styles) => ({
          ...styles,
          padding: '0px 10px 0px 8px'
        }),
        input: (styles) => ({
          ...styles,
          fontFamily: 'Inter',
          fontWeight: '400',
          fontSize: '12px',
        }),
        placeholder: (styles) => ({
          ...styles,
          fontFamily: 'Inter',
          fontWeight: '400',
          fontSize: '12px',
          color: '#B1B1B1'
        }),
        option: (styles, { isFocused, isSelected }) => ({
          ...styles,
          backgroundColor: isFocused ? '#f5f5f5' : isSelected ? '#e6e6e6' : undefined,
          color: '#333',
          fontFamily: 'Inter',
          fontWeight: isSelected ? '600' : '400',
          fontSize: '12px',
          cursor: 'pointer'
        }),
        singleValue: (styles) => ({
          ...styles,
          color: '#333',
          fontFamily: 'Inter',
          fontWeight: '400',
          fontSize: '12px',
        }),
        dropdownIndicator: (styles) => ({
          ...styles,
          color: '#2D2D2D',
          padding: '0px'
        }),
        menu: (provided) => ({
          ...provided,
          maxHeight: '200px',
        }),
        menuList: (provided) => ({
          ...provided,
          maxHeight: '180px',
          overflowY: 'auto'
        }),
    }

    const [getAllMeetingCategory] = useLazyQuery(GET_ALL_MEETING_CATEGORIES, {
        fetchPolicy: FETCH_POLICY,
        onCompleted: (data) => {
          methods.setValue('category', data?.allMeetingCategories?.nodes?.[0]?.mcId)
            setCategoryList(data?.allMeetingCategories?.nodes.map((val: MeetingCategoryType) => ({
                value: val?.mcId,
                label: val?.mcOriginalType 
            })));
        },
        onError: (err) => {
          error(err?.message);
        }
    })

    useEffect(() => {
        getAllMeetingCategory();
    },[])

    const [createMeeting, { loading: createLoading }] = useMutation(CREATE_PTA_MEETING, {
        fetchPolicy: FETCH_POLICY,
        onCompleted: () => {
            success("Meeting created succesfully");
            setShowCreateScheduling(false);
            reset();
        },
        onError: (err) => {
            error(err?.message);
        }
    })

    const [ updatePtaMeeting, { loading: EditLoading } ] = useMutation(EDIT_PTA_MEETING, {
        fetchPolicy: FETCH_POLICY,
        onCompleted: (data) => {
          success('Meeting updated succesfully');
          setShowCreateScheduling(false);
          if (fetchUpcomingSessions) {
            fetchUpcomingSessions();
          }
          if (setEditClicked) {
            setEditClicked(false);
          }
          reset();
        },
        onError: (err) => {
          error(err?.message);
        }
    });

    useEffect(() => {        
        if (startTime && endTime && isSubmitted) {
            trigger('timeRange'); // Trigger the timeRange validation
        }
    }, [startTime, endTime, trigger]); 

    useEffect(()=>{
      if(dateValue){
        trigger('endTime');
        trigger('startTime');
        trigger('timeRange');
      }
    },[dateValue,trigger])
    
    const convertToUTC = (dateValue: string, timeValue:string) => {
    // Ensure dateValue is a string
    const dateStr = String(dateValue);

    // Extract date part from the dateValue string
    const datePart = dateStr.split(' ').slice(0, 4).join(' ');
    // Combine the date part with timeValue
    const combinedDateTime = `${datePart} ${timeValue}`;

    // Create a moment object from the combined date and time
    const localDate = moment(combinedDateTime, 'ddd MMM DD YYYY hh:mm A');
    // Get the local timezone
    const localTimezone = moment.tz.guess();

    // Convert the local date to UTC
    const utcDate = localDate.tz(localTimezone).utc();

    // Format the UTC date as required
    return utcDate.utc().format();
    };

    const onSubmitForm: SubmitHandler<IFormInput> = (data) => {

        trackButtonClick(ButtonClickEvents?.PTA_SCHEDULE_MEETING_SAVE_BUTTON_CLICK, {
            description : "User clicked save button in schedule meeting in PTA",
            page: 'Admin PTA meeting schedule'
        });

        const startDate = convertToUTC(data.dateValue, data.startTime);
        const endDate = convertToUTC(data.dateValue, data.endTime);
        const MEETING_STATUS_STARTED = 'NOT_STARTED';

        if (editClicked && !areAnyChanges(data)) {
            if (setEditClicked) {
                setEditClicked(false);
            }
            setShowCreateScheduling(false);
            reset();
            return;
        }
        
        if (editClicked) {
            updatePtaMeeting({
                variables: {
                    input: {
                        meetingPatch: {
                        mTopic: data?.description?.trim(),
                        mcId: data?.category,
                        mStartTime: startDate,
                        mEndTime: endDate,
                        ...(meetService === JITSI ? { meetingType: "JITSI" } : {}),
                        },
                        mId: clickedSessionDetails?.mId
                    }
                }
            })
        }
        else {
            createMeeting({
                variables: {
                    input: {
                        meeting: {
                        mTopic: data?.description?.trim(),
                        mcId: data?.category,
                        mStartTime: startDate,
                        mEndTime: endDate,
                        mStatus: MEETING_STATUS_STARTED,
                        ...(meetService === JITSI ? { meetingType: "JITSI" } : {}),
                        }
                    }
                }
            })
        }
    }

    const areAnyChanges = (data: IFormInput) => {
        let returnValue;
        if(clickedSessionDetails){
            returnValue = (dayjs(data?.dateValue).format('YYYY-MM-DD') !== dayjs(clickedSessionDetails?.mStartTime).format('YYYY-MM-DD') ||
            data?.startTime !== (clickedSessionDetails?.mStartTime && formatStartAndEndTime(clickedSessionDetails?.mStartTime)) ||
            data?.endTime !== (clickedSessionDetails?.mEndTime && formatStartAndEndTime(clickedSessionDetails?.mEndTime)) || 
            data?.description !== clickedSessionDetails?.mTopic ||
            data?.category !== clickedSessionDetails?.mcId )

            return returnValue;
        }
    }

    const onClickClearBtn = () => {
        trackButtonClick(ButtonClickEvents?.PTA_SCHEDULE_MEETING_CLEAR_BUTTON_CLICK, {
            description : "User clicked clear button in schedule meeting in PTA",
            page: 'Admin PTA meeting schedule'
        });
        reset({
            startTime: "",
            endTime: "",
            dateValue: null,
            timeRange: "",
            description: "",
            category: ""
        })
    }

    useEffect(() => {
        if(!editClicked){
            const start = dayjs().add(meetService === 'CHIME' ? 1 : 7, 'minutes'); // Setting start time 7 minutes from now
            const end = dayjs().add(meetService === 'CHIME' ? 61 : 67, 'minutes');  // End time 60 minutes after the start time
            setValue('dateValue', dayjs(new Date()));
            setValue('startTime', start.format('hh:mm A'));
            setValue('endTime', end.format('hh:mm A'));
            setValue('description', "");
        }
    }, [editClicked])

    useEffect(() => {
        if (clickedSessionDetails && editClicked) {            
            reset({
                startTime: formatStartAndEndTime(clickedSessionDetails?.mStartTime),
                endTime: formatStartAndEndTime(clickedSessionDetails?.mEndTime),
                dateValue: dayjs(clickedSessionDetails?.mStartTime),
                description: clickedSessionDetails?.mTopic,
                category: clickedSessionDetails?.mcId
            })
        }
    },[clickedSessionDetails])

    const handleDelete = () => {
        if (setOpenConfirmationPopup) {
            setOpenConfirmationPopup(true);
        }
    }
   
  return (
    <>
        <div className='scheduleHeadingContainerPta'>Schedule your PTA Meetings</div>
        <div className='scheduleFormContainerPta'>
            <FormProvider {...methods}>
                <form className='formStyle' autoComplete="off" onSubmit={handleSubmit(onSubmitForm)}>
                      <div className='topContainer'>
                          <div className='topicAndDescriptionCtn'>
                              <div className='messageContainer'>
                                  <label className='textareaLabelStyle'>Topic</label>
                                  <textarea
                                      {...register('description')}
                                      id="textareaBox"
                                      placeholder={'Messages goes here...'}
                                      className='textareaStyle'
                                  />
                                  {errors.description && isSubmitted && <span className="error">{errors?.description && errors?.description?.message}</span>}
                              </div>
                          </div>
                        <div className='dataTimeContainer'>
                                <div className='DateContainer'>
                                    <label className='dateLabelStyle'>Date</label>
                                    <Controller
                                        control={control}
                                        name={'dateValue'}
                                        render={({ field }) => (
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DemoContainer components={['DesktopDatePicker']} sx={{overflow: 'hidden', paddingTop: 0}}>
                                                <DesktopDatePicker
                                                disablePast
                                                {...field}
                                                value={field.value || null}
                                                onChange={(e, value) => {
                                                    field.onChange(e)
                                                }}
                                                format="DD/MM/YY"
                                                minDate={dayjs().startOf('year')}
                                                maxDate={dayjs().endOf('year')}
                                                slotProps={{
                                                    textField: {
                                                    onKeyDown: (e) => {e.preventDefault()},
                                                    placeholder: 'DD/MM/YY',
                                                    sx: {
                                                        '& .MuiInputBase-root': {
                                                    backgroundColor: isMobileScreen ? COLOR_WHITE : '#f0f3f5',
                                                    width: '162px',
                                                    height: '35px',
                                                    outline : 'none',
                                                    border: 'none',
                                                    '&:focus': {
                                                        outline: 'none',
                                                    },
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        border: 'none',
                                                    },
                                                    '& .MuiInputBase-input::placeholder': {
                                                        opacity: 1,
                                                        fontFamily: 'Inter',
                                                        color: '#616161', // Change placeholder color
                                                        fontSize: '12px', // Change placeholder font size,
                                                        fontWeight: 400,
                                                        '&:focus': {
                                                        fontSize: '12px',
                                                        color: '#616161', // Change placeholder color
                                                    },
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '12px', // Set font size for entered values
                                                    },
                                                    },
                                                    },
                                                }}
                                                />
                                                </DemoContainer>
                                            </LocalizationProvider>
                                        )}
                                    />
                                    {errors.dateValue && isSubmitted && <span className="error">{errors.dateValue.message as ReactNode}</span>}
                                </div>
                                <div className='startTimeContainer'>
                                    <label className='dateLabelStyle'>Start Time</label>
                                    {/* <TimeTextField name={'startTime'}/> */}
                                    <Controller
                                        name="startTime"
                                        control={control}
                                        render={({ field }) => {
                                            // Defining the Threshold time as current time plus 7 minutes
                                            const threshold = dayjs().add(meetService === 'CHIME' ? 1 : 7, "minutes");
                                            // Checking if the selected date is today
                                            const isToday = dateValue && dayjs(dateValue).isSame(threshold, 'day');
                                            // Setting minTime to thresholdTime if the selected date is today
                                            const minTime = isToday ? threshold : undefined;

                                            return (
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <DesktopTimePicker
                                                        sx={customStyle}
                                                        {...field}
                                                        value={field.value ? dayjs(field.value, 'hh:mm A') : null}
                                                        onChange={(newValue) => field.onChange(newValue ? newValue.format('hh:mm A') : '')}
                                                        format="hh:mm A"
                                                        timeSteps={{minutes:1}}
                                                        minTime={minTime}
                                                        slotProps={{
                                                            textField: {
                                                                placeholder: "00:00 AM",
                                                            }
                                                        }}
                                                        closeOnSelect={false}
                                                    />
                                                </LocalizationProvider>
                                            )
                                        }}
                                    />
                                    {errors.startTime && isSubmitted && <span className="error">{errors.startTime.message as ReactNode}</span>}
                                </div>
                                <div className='endTimeContainer'>
                                    <label className='dateLabelStyle'>End Time</label>
                                    {/* <TimeTextField name={"endTime"}/> */}
                                    <Controller
                                        name="endTime"
                                        control={control}
                                        render={({ field }) => (
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DesktopTimePicker
                                                    sx={customStyle}
                                                    {...field}
                                                    value={field.value ? dayjs(field.value, 'hh:mm A') : null}
                                                    onChange={(newValue) => field.onChange(newValue ? newValue.format('hh:mm A') : '')}
                                                    format="hh:mm A"
                                                    timeSteps={{minutes:1}}
                                                    slotProps={{
                                                        textField: {
                                                            placeholder: "00:00 AM",
                                                        }
                                                    }}
                                                    closeOnSelect={false}
                                                />
                                            </LocalizationProvider>
                                        )}
                                    />
                                    {errors.endTime && isSubmitted && <span className="error">{errors.endTime.message as ReactNode}</span>}
                                    {errors.timeRange && isSubmitted && (!errors.startTime && !errors.endTime) && <span className="error">{errors.timeRange.message}</span>}
                                </div>
                        </div>
                        <div className='topicAndDescriptionCtn'>
                              <div className='messageContainer'>
                                  <label className='textareaLabelStyle'>Meeting For</label>
                                  <Select
                                    onChange={(selectedOption) => {
                                        if (selectedOption) {
                                        const option = selectedOption as CategoryOption;
                                        methods.setValue('category', option.value);
                                        clearErrors('category');
                                        } else {
                                        methods.setValue('category', '');
                                        }
                                    }}
                                    defaultValue={categoryList?.[0]}
                                    value={categoryList.find(option => option?.value === getValues('category'))}
                                    classNamePrefix="dropdown-indicator"
                                    styles={customStyleSelect}
                                    options={categoryList}
                                    />
                                  {errors.category && isSubmitted && <span className="error">{errors?.category && errors?.category?.message}</span>}
                              </div>
                          </div>
                    </div>
                    <div className='bottomContainer'>
                        {
                            clickedSessionDetails ?
                            <div className='clearButtonContainer' onClick={handleDelete}>
                                <img src={teacherDeleteIcon} alt="deleteIcon" className='deleteIconStyle' />
                            </div>
                            :
                            <div className='clearButtonContainer' onClick={onClickClearBtn}>
                                Clear
                            </div>
                        }
                            <button className='saveButtonStyle' type='submit' disabled={ createLoading || EditLoading }>
                                Save
                            </button>
                    </div>
                </form>
            </FormProvider>
        </div>
    </>
  )
}

export default CreateSchedulePta;
